import { hash } from '@ember/helper';
import { action } from '@ember/object';
import { trackedSet } from '@ember/reactive/collections';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import {
  PrismCard,
  PrismInput,
  PrismMessage,
  PrismSelectInput,
  PrismTable,
} from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import { TOAST_TIMEOUT_LONG } from '@mylighthouse/prism-foundation';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import TransactionCellsTdButton from 'frontend-payments/components/transaction/cells/td-button';
import TransactionCellsTdStatus from 'frontend-payments/components/transaction/cells/td-status';
import type { Account, Transaction } from 'frontend-payments/models';
import {
  TRANSACTION_STATUS,
  TRANSACTION_STATUS_TRANSLATION_KEYS,
  TRANSACTION_TYPE_TRANSLATION_KEYS,
  type TransactionStatus,
} from 'frontend-payments/models/transaction';
import { downloadPdf } from 'frontend-payments/utils/download-pdf';

import styles from './table-invoices.module.css';

type StatusId = TransactionStatus | typeof ALL_STATUSES;

type StatusOption = Option & {
  id: StatusId;
};

const COLUMNS = {
  INVOICE_NUMBER: 'invoiceNumber',
  INVOICE_DATE: 'invoiceDate',
  INVOICE_DATE_FORMATTED: 'invoiceDateFormatted',
  DUE_DATE: 'dueDate',
  DUE_DATE_FORMATTED: 'dueDateFormatted',
  INVOICE_TOTAL: 'invoiceTotal',
  INVOICE_REMAINING: 'invoiceRemaining',
  STATUS: 'status',
  PAY_NOW: 'payNow',
  INVOICE: 'invoice',
  RECEIPT: 'receipt',
} as const;

const ALL_STATUSES = 'all_statuses' as const;

interface TransactionTableInvoicesSignature {
  Args: {
    account?: Account | null;
    isTransactionRequestLoading: boolean;
    onPayNowClick?: (transaction: Transaction) => void;
    transactions: Transaction[];
  };
}

export default class TransactionTableInvoices extends Component<TransactionTableInvoicesSignature> {
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @service declare session: Services['session'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked searchField = '';
  @tracked selectedStatusId: StatusId = ALL_STATUSES;

  downloadingUrls = trackedSet<string>();

  initialSorts = [
    {
      valuePath: COLUMNS.INVOICE_DATE,
      isAscending: false,
    },
  ];

  downloadPdfTask = task(
    async (downloadUrl: string, filename: string): Promise<void> => {
      this.downloadingUrls.add(downloadUrl);

      try {
        await downloadPdf(downloadUrl, filename);
      } catch (error) {
        this.errorTracking.captureException(error);
        this.toast.error(
          this.intl.t('payments.invoice.table.download_error_message'),
          {
            title: this.intl.t('payments.invoice.table.download_error_title'),
            timeout: TOAST_TIMEOUT_LONG,
            showIcon: true,
          },
        );
      } finally {
        this.downloadingUrls.delete(downloadUrl);
      }
    },
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get columns() {
    return [
      {
        name: this.intl.t('payments.invoice.table.header.invoice_number'),
        valuePath: COLUMNS.INVOICE_NUMBER,
        textAlign: 'left' as const,
        width: 150,
      },
      {
        name: this.intl.t('payments.invoice.table.header.invoice_date'),
        valuePath: COLUMNS.INVOICE_DATE,
        displayValuePath: COLUMNS.INVOICE_DATE_FORMATTED,
        textAlign: 'left' as const,
        minWidth: 100,
        maxWidth: 150,
      },
      {
        name: this.intl.t('payments.invoice.table.header.due_date'),
        valuePath: COLUMNS.DUE_DATE,
        displayValuePath: COLUMNS.DUE_DATE_FORMATTED,
        textAlign: 'left' as const,
        minWidth: 100,
        maxWidth: 150,
      },
      {
        name: this.intl.t('payments.invoice.table.header.invoice_total'),
        valuePath: COLUMNS.INVOICE_TOTAL,
        textAlign: 'right' as const,
        isSortable: false,
        minWidth: 100,
        maxWidth: 150,
      },
      {
        name: this.intl.t('payments.invoice.table.header.invoice_remaining'),
        valuePath: COLUMNS.INVOICE_REMAINING,
        textAlign: 'right' as const,
        isSortable: false,
        minWidth: 100,
        maxWidth: 150,
      },
      {
        name: this.intl.t('payments.invoice.table.header.status'),
        valuePath: `${COLUMNS.STATUS}`,
        textAlign: 'left' as const,
        tdComponent: TransactionCellsTdStatus,
        isSortable: false,
        minWidth: 80,
      },
      {
        name: '',
        valuePath: COLUMNS.PAY_NOW,
        textAlign: 'left' as const,
        tdComponent: TransactionCellsTdButton,
        isSortable: false,
        width: 100,
      },
      {
        name: '',
        valuePath: COLUMNS.INVOICE,
        tdComponent: TransactionCellsTdButton,
        isSortable: false,
        width: 120,
      },
      {
        name: '',
        valuePath: COLUMNS.RECEIPT,
        tdComponent: TransactionCellsTdButton,
        isSortable: false,
        width: 110,
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filteredRows() {
    let filteredTransactions = this.rows;

    // Filter by search field
    if (this.searchField) {
      const searchLower = this.searchField.toLowerCase();
      filteredTransactions = filteredTransactions.filter((row) => {
        return row.invoiceNumber.toLowerCase().includes(searchLower);
      });
    }

    // Filter by status
    if (this.selectedStatus?.['value']) {
      filteredTransactions = filteredTransactions.filter((row) => {
        const statusType = row.status.statusType;
        const selectedStatusId = this.selectedStatus?.id;

        // When "unpaid" is selected, also show "overdue" invoices
        if (selectedStatusId === TRANSACTION_STATUS.UNPAID) {
          return (
            statusType === TRANSACTION_STATUS.UNPAID ||
            statusType === TRANSACTION_STATUS.OVERDUE
          );
        }

        return statusType === selectedStatusId;
      });
    }

    return filteredTransactions;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rows() {
    return this.args.transactions.map((transaction) => {
      return {
        [COLUMNS.INVOICE_NUMBER]: transaction.transactionNumber,
        [COLUMNS.INVOICE_DATE]:
          transaction.transactionDate?.format('YYYY-MM-DD'),
        [COLUMNS.INVOICE_DATE_FORMATTED]: transaction.transactionDateFormatted,
        [COLUMNS.DUE_DATE]: transaction.dueDate?.format('YYYY-MM-DD'),
        [COLUMNS.DUE_DATE_FORMATTED]: transaction.dueDateFormatted,
        [COLUMNS.INVOICE_TOTAL]: transaction.invoiceTotal,
        [COLUMNS.INVOICE_REMAINING]: transaction.invoiceRemaining,
        [COLUMNS.STATUS]: {
          badgeType: transaction.badgeType,
          formattedStatus: transaction.statusFormatted,
          statusType: transaction.status,
        },
        [COLUMNS.PAY_NOW]: this.getPayNowButtonConfig(transaction),
        [COLUMNS.INVOICE]: this.getInvoicePdfButtonConfig(transaction),
        [COLUMNS.RECEIPT]: this.getReceiptPdfButtonConfig(transaction),
      };
    });
  }

  get selectedStatus(): StatusOption | null {
    return (
      this.statusOptions.find(
        (statusFilterOption) => statusFilterOption.id === this.selectedStatusId,
      ) ?? null
    );
  }

  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get statusOptions() {
    const allStatusesOption = {
      label: this.intl.t('payments.filter.all_statuses'),
      value: null,
      id: ALL_STATUSES,
    };

    const statusOptions = Object.values(TRANSACTION_STATUS)
      .map((status) => ({
        label: this.intl.t(TRANSACTION_STATUS_TRANSLATION_KEYS[status]),
        value: status,
        id: status,
      }))
      .sort((a, b) => {
        return a.label.localeCompare(b.label);
      });

    return [allStatusesOption, ...statusOptions];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getInvoicePdfButtonConfig(transaction: Transaction) {
    const label = this.intl.t(
      TRANSACTION_TYPE_TRANSLATION_KEYS[transaction.transactionType],
    );

    if (transaction.pdfUrl) {
      const filename = `${transaction.transactionType}-${transaction.transactionNumber}.pdf`;
      const pdfUrl = transaction.pdfUrl;
      return {
        value: pdfUrl,
        label,
        iconBefore: 'download',
        buttonType: 'secondary',
        isLoading: this.downloadingUrls.has(pdfUrl),
        isDisabled: this.downloadingUrls.has(pdfUrl),
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        onClick: () => {
          this.usageAnalytics.track('Invoice Downloaded', {
            eventTrigger: 'invoice-table',
          });
          void this.downloadPdfTask.perform(pdfUrl, filename);
        },
      };
    }

    return {
      value: '',
      label,
      iconBefore: 'download',
      buttonType: 'secondary',
      isDisabled: true,
      disabledText: this.intl.t('payments.invoice.table.pdf_unavailable'),
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getPayNowButtonConfig(transaction: Transaction) {
    const hasActivePaymentMethod =
      this.args.account?.payments?.recurringPaymentMethod;
    const isOpenOrOverdueInvoice =
      transaction.transactionType === 'invoice' &&
      (transaction.status === TRANSACTION_STATUS.UNPAID ||
        transaction.status === TRANSACTION_STATUS.OVERDUE);
    if (
      transaction.isCreditNote ||
      transaction.isDebitNote ||
      !isOpenOrOverdueInvoice
    ) {
      return null;
    }

    if (hasActivePaymentMethod || this.session.isStaffUser) {
      return {
        value: '',
        label: this.intl.t('payments.pay_now'),
        isDisabled: true,
        disabledText: this.session.isStaffUser
          ? ''
          : this.intl.t('payments.invoice.table.pay_now_disabled'),
      };
    }

    if (transaction.paymentUrl) {
      return {
        value: transaction.paymentUrl,
        label: this.intl.t('payments.pay_now'),
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        onClick: () => {
          this.usageAnalytics.track('Manually Paid', {
            eventTrigger: 'invoice-table',
          });

          this.args.onPayNowClick?.(transaction);
        },
      };
    }

    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getReceiptPdfButtonConfig(transaction: Transaction) {
    if (transaction.isCreditNote || transaction.isDebitNote) {
      return null;
    }

    if (transaction.receiptUrl) {
      const filename = `receipt-${transaction.transactionNumber}.pdf`;
      const receiptUrl = transaction.receiptUrl;
      return {
        value: receiptUrl,
        label: this.intl.t('payments.invoice.table.receipt'),
        iconBefore: 'download',
        buttonType: 'secondary',
        isLoading: this.downloadingUrls.has(receiptUrl),
        isDisabled: this.downloadingUrls.has(receiptUrl),
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        onClick: () => {
          this.usageAnalytics.track('Receipt Downloaded', {
            eventTrigger: 'invoice-table',
          });
          void this.downloadPdfTask.perform(receiptUrl, filename);
        },
      };
    }

    // Show disabled receipt button if no receipt URL is available
    return {
      value: '',
      label: this.intl.t('payments.invoice.table.receipt'),
      iconBefore: 'download',
      buttonType: 'secondary',
      isDisabled: true,
      disabledText: this.intl.t('payments.invoice.table.receipt_unavailable'),
    };
  }

  @action
  updateSearchField(event: Event): void {
    this.searchField = (event.target as HTMLInputElement).value;
  }

  @action
  updateSelectedStatus(selectedOption: StatusOption): void {
    this.selectedStatusId = selectedOption.id;

    this.usageAnalytics.track('Invoice Status Filtered', {
      eventTrigger: 'dropdown-menu',
    });
  }

  <template>
    <PrismCard @contentLayout="noPadding">
      <:content>
        <div class={{styles.invoices-container}}>
          <PrismSelectInput
            @onChange={{this.updateSelectedStatus}}
            @options={{this.statusOptions}}
            @value={{this.selectedStatus}}
          />
          <div class={{styles.invoices-search}}>
            <PrismInput
              @iconAfter={{hash name="search"}}
              @onInput={{this.updateSearchField}}
              @placeholder={{t "payments.invoice.table.search_for_invoice"}}
              @removePadding={{true}}
              @value={{this.searchField}}
            />
          </div>
        </div>
        <div class={{styles.invoices-table}}>
          <PrismTable
            @columns={{this.columns}}
            @enableAutoSortsUpdate={{true}}
            @initialSorts={{this.initialSorts}}
            @isLoading={{@isTransactionRequestLoading}}
            @nrOfLoadingRows={{10}}
            @removeTableBorder={{true}}
            @rows={{this.filteredRows}}
            @widthConstraint="gte-container-slack"
          >
            <:emptyState>
              <div class="flex h-full w-full items-center justify-center">
                <PrismMessage
                  @illustration="100x100_search"
                  @title={{t "payments.invoice.table.empty_state"}}
                />
              </div>
            </:emptyState>
          </PrismTable>
        </div>
      </:content>
    </PrismCard>
  </template>
}
